"use client";

import { useEffect, useMemo, useState } from "react";
import { api, RouterOutputs } from "@/trpc/react";
import { cn } from "@workspace/ui/lib/utils";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { 
  Edit2, 
  Trash2, 
  Plus, 
  GripVertical,
  FolderPlus,
  MoreVertical,
  Sparkles,
  ExternalLink,
} from "lucide-react";
import NextLink from "next/link";
import { AddLinkDialog } from "./add-link-dialog";
import { AddCategoryDialog } from "./add-category-dialog";
import { EditLinkDialog } from "./edit-link-dialog";
import { toast } from "sonner";
import type { Platform } from "./links-platform-content";
import { useSortable } from "@dnd-kit/sortable";
import { VerticalSortable } from "@/components/admin/vertical-sortable";
// Using useSortable's provided transform/transition; lists use VerticalSortable wrapper

interface LinksAdminViewProps {
  platform?: Platform;
  hideHeader?: boolean;
}

type LinkItem = RouterOutputs['link']['getAll'][number];
type CategoryWithLinks = RouterOutputs['category']['getAll'][number] & { links: LinkItem[] };

export function LinksAdminView({ platform = 'default', hideHeader = false }: LinksAdminViewProps) {
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [editLinkOpen, setEditLinkOpen] = useState(false);
  const [creatingFeatured, setCreatingFeatured] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedLink, setSelectedLink] = useState<RouterOutputs['link']['getAll'][number] | null>(null);
  
  const utils = api.useUtils();
  const { data: categories, isLoading: categoriesLoading } = api.category.getAll.useQuery();
  const { data: links, isLoading: linksLoading } = api.link.getAll.useQuery();
  const reorderCategories = api.category.reorder.useMutation({
    onMutate: async (vars) => {
      await utils.category.getAll.cancel();
      const previous = utils.category.getAll.getData();
      // Optimistically reorder cache and update order values
      utils.category.getAll.setData(undefined, (old) => {
        if (!old) return old;
        const idIndex: Record<string, number> = {};
        vars.ids.forEach((id, idx) => (idIndex[id] = idx + 1));
        return old
          .map((c) => ({ ...c, order: idIndex[c.id] ?? c.order }))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      });
      return { previous } as const;
    },
    onError: (_err, _vars, ctx) => {
      utils.category.getAll.setData(undefined, ctx?.previous);
      toast.error("Failed to save category order");
    },
    onSuccess: () => {
      toast.success("Category order saved");
    },
    onSettled: () => {
      utils.category.getAll.invalidate();
    },
  });
  
  // Helper function to get the appropriate URL based on platform
  const getPlatformUrl = (link: LinkItem) => {
    switch (platform) {
      case 'instagram':
        return link.instagramUrl || link.url;
      case 'tiktok':
        return link.tiktokUrl || link.url;
      default:
        return link.url;
    }
  };
  const isInternal = (href: string | undefined | null) => !!href && href.startsWith('/') && !href.startsWith('//');
  
  const deleteLink = api.link.delete.useMutation({
    onSuccess: () => {
      utils.link.getAll.invalidate();
      toast.success("Link deleted");
    },
    onError: () => {
      toast.error("Failed to delete link");
    },
  });
  
  const deleteCategory = api.category.delete.useMutation({
    onSuccess: () => {
      utils.category.getAll.invalidate();
      utils.link.getAll.invalidate();
      toast.success("Category deleted");
    },
    onError: () => {
      toast.error("Failed to delete category");
    },
  });
  
  const isLoading = categoriesLoading || linksLoading;
  
  const [isMounted, setIsMounted] = useState(false);

  // No local link order state; derive from TRPC data + optimistic cache updates

  const reorderLinks = api.link.reorder.useMutation({
    onMutate: async (vars) => {
      await utils.link.getAll.cancel();
      const previous = utils.link.getAll.getData();
      // Optimistically update link.order in cache for the affected category
      utils.link.getAll.setData(undefined, (old) => {
        if (!old) return old;
        const indexById: Record<string, number> = {};
        vars.ids.forEach((id, idx) => (indexById[id] = idx + 1));
        return old.map((l) => (vars.ids.includes(l.id) ? { ...l, order: indexById[l.id] ?? l.order } : l));
      });
      return { previous } as const;
    },
    onError: (_err, _vars, ctx) => {
      // Rollback cache
      utils.link.getAll.setData(undefined, ctx?.previous);
      toast.error("Failed to save link order");
    },
    onSuccess: () => {
      toast.success("Link order saved");
    },
    onSettled: () => {
      utils.link.getAll.invalidate();
    },
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // No effect: the list renders from TRPC query order and optimistic cache

  // Build categories + links, then apply local order for rendering
  const categoriesWithLinksUnordered: CategoryWithLinks[] = useMemo(() => {
    const cats = categories ?? [];
    const lks = links ?? [];
    return cats.map((category) => ({
      ...category,
      links: lks
        .filter((link) => link.categoryId === category.id)
        .sort((a, b) => a.order - b.order),
    }));
  }, [categories, links]);

  const categoriesWithLinks: CategoryWithLinks[] = useMemo(() => {
    return [...categoriesWithLinksUnordered].sort((a, b) => a.order - b.order);
  }, [categoriesWithLinksUnordered]);
  
  const handleAddLink = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setCreatingFeatured(false);
    setAddLinkOpen(true);
  };
  const handleAddFeatured = () => {
    setSelectedCategoryId(null);
    setCreatingFeatured(true);
    setAddLinkOpen(true);
  };
  
  const handleEditLink = (link: RouterOutputs['link']['getAll'][number]) => {
    setSelectedLink(link);
    setEditLinkOpen(true);
  };
  
  const handleDeleteLink = async (linkId: string) => {
    if (confirm("Are you sure you want to delete this link?")) {
      await deleteLink.mutateAsync({ id: linkId });
    }
  };
  
  const handleDeleteCategory = async (categoryId: string) => {
    if (confirm("Are you sure you want to delete this category and all its links?")) {
      await deleteCategory.mutateAsync({ id: categoryId });
    }
  };

  // Drag logic is encapsulated by VerticalSortable wrappers
  
  if (isLoading || !categories || !links) {
    return (
      <div className="bg-background">
        <div className="container mx-auto py-2 max-w-5xl lg:max-w-6xl xl:max-w-7xl">
          <div className="animate-pulse space-y-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-3">
                <div className="h-8 bg-muted rounded w-48" />
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      {/* Admin header with actions */}
      <div className="container mx-auto py-2 max-w-5xl lg:max-w-6xl xl:max-w-7xl">
        {!hideHeader && (
          <header className="mb-12">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">Links Admin</h1>
                <p className="text-muted-foreground">Manage your links and categories.</p>
              </div>
              <Button 
                onClick={() => setAddCategoryOpen(true)}
                size="sm"
                variant="outline"
                className="gap-2"
              >
                <FolderPlus className="w-4 h-4" />
                Add Category
              </Button>
            </div>
          </header>
        )}
        {/* Featured links section */}
        <div className="mb-6">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <h2 className="m-0 text-base font-semibold text-foreground">Featured</h2>
            </div>
            <Button onClick={handleAddFeatured} size="sm" variant="ghost" className="gap-2">
              <Plus className="w-3 h-3" />
            </Button>
          </div>
          {(() => {
            const featured = (links ?? []).filter((l: any) => l.featured);
            if (featured.length === 0) {
              return (
                <div className="text-muted-foreground text-sm py-4 border border-dashed rounded-md text-center">
                  No featured links yet.
                </div>
              );
            }
            return (
              <ul className="space-y-2 list-none pl-0">
                {featured.map((link: any) => {
                  const href = getPlatformUrl(link);
                  const internal = isInternal(href);
                  return (
                    <li key={link.id} className="group/link flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-4 h-4 text-amber-500">
                        <Sparkles className="w-3 h-3" />
                      </span>
                      {internal ? (
                        <NextLink href={href} className="block flex-1">
                          <div className="inline-flex items-center gap-2 text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary/60">
                            <span className="font-medium">{link.name}</span>
                            <ExternalLink className="w-3 h-3 opacity-50 group-hover/link:opacity-100" />
                          </div>
                          {link.description ? (
                            <p className="mt-1 text-sm text-muted-foreground">{link.description}</p>
                          ) : null}
                        </NextLink>
                      ) : (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="block flex-1">
                          <div className="inline-flex items-center gap-2 text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary/60">
                            <span className="font-medium">{link.name}</span>
                            <ExternalLink className="w-3 h-3 opacity-50 group-hover/link:opacity-100" />
                          </div>
                          {link.description ? (
                            <p className="mt-1 text-sm text-muted-foreground">{link.description}</p>
                          ) : null}
                        </a>
                      )}
                      <div className="hidden sm:flex items-center gap-1 transition-opacity">
                        <Button onClick={() => handleEditLink(link)} size="sm" variant="ghost" className="h-6 px-1.5">
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button onClick={() => handleDeleteLink(link.id)} size="sm" variant="ghost" className="h-6 px-1.5 text-destructive hover:text-destructive">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </div>

        {/* Markdown-style content with admin controls */}
        <div className="max-w-none">
          {isMounted ? (
            <VerticalSortable
              items={categoriesWithLinks}
              getId={(c) => c.id}
              onReorder={(ids) => reorderCategories.mutate({ ids })}
              className="list-none pl-0 space-y-8"
            >
              {(category) => (
                <CategoryItem
                  key={category.id}
                  category={category}
                  onAddLink={handleAddLink}
                  onDeleteCategory={handleDeleteCategory}
                  onEditLink={handleEditLink}
                  onDeleteLink={handleDeleteLink}
                  getPlatformUrl={getPlatformUrl}
                  isInternal={isInternal}
                  onReorderLinks={(categoryId, ids) => reorderLinks.mutate({ categoryId, ids })}
                />
              )}
            </VerticalSortable>
          ) : (
            // Static non-DnD rendering for SSR to avoid hydration mismatch
            <>
              {categoriesWithLinks.map((category) => (
                <div
                  key={category.id}
                  className={cn(
                    "group/category py-8",
                  )}
                >
                  {/* Category heading with actions */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 text-muted-foreground">
                        <GripVertical className="w-4 h-4" />
                      </span>
                      <h2 className="text-2xl font-semibold m-0 text-foreground">
                        {category.name}
                      </h2>
                      <span className="text-sm text-muted-foreground">
                        ({category.links.length})
                      </span>
                    </div>

                    {/* Desktop action buttons */}
                    <div className="hidden sm:flex items-center gap-1">
                      <Button
                        onClick={() => handleAddLink(category.id)}
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteCategory(category.id)}
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>

                    {/* Mobile dropdown menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild className="sm:hidden">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                          <MoreVertical className="w-4 h-4" />
                          <span className="sr-only">Category actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleAddLink(category.id)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Link
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Category
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Links list with admin controls */}
                  {category.links.length > 0 ? (
                    <ul className="space-y-2 list-none pl-2">
                      {category.links.map((link) => {
                        const href = getPlatformUrl(link);
                        const internal = isInternal(href);
                        return (
                          <li key={link.id} className="group/link flex items-start gap-2">
                            <span className="inline-flex items-center justify-center w-4 h-6 text-muted-foreground mt-0.5">
                              <GripVertical className="w-3 h-3" />
                            </span>
                            {internal ? (
                              <NextLink href={href} className="block flex-1">
                                <div className="inline-flex items-center gap-2 text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary/60">
                                  <span className="font-medium">{link.name}</span>
                                  <ExternalLink className="w-3 h-3 opacity-50 group-hover/link:opacity-100" />
                                </div>
                                {link.description ? (
                                  <p className="mt-1 text-sm text-muted-foreground">{link.description}</p>
                                ) : null}
                              </NextLink>
                            ) : (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="block flex-1">
                                <div className="inline-flex items-center gap-2 text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary/60">
                                  <span className="font-medium">{link.name}</span>
                                  <ExternalLink className="w-3 h-3 opacity-50 group-hover/link:opacity-100" />
                                </div>
                                {link.description ? (
                                  <p className="mt-1 text-sm text-muted-foreground">{link.description}</p>
                                ) : null}
                              </a>
                            )}

                            {/* Desktop action buttons */}
                            <div className="hidden sm:flex items-center gap-1 transition-opacity self-center">
                              <Button
                                onClick={() => handleEditLink(link)}
                                size="sm"
                                variant="ghost"
                                className="h-6 px-1.5"
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteLink(link.id)}
                                size="sm"
                                variant="ghost"
                                className="h-6 px-1.5 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>

                            {/* Mobile dropdown menu */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild className="sm:hidden self-center">
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                  <span className="sr-only">Link actions</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditLink(link)}>
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteLink(link.id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="text-muted-foreground text-sm py-4 border border-dashed rounded-md text-center">
                      No links in this category.{" "}
                      <button
                        onClick={() => handleAddLink(category.id)}
                        className="text-primary hover:underline"
                      >
                        Add one
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          
          {/* Empty state */}
          {categoriesWithLinks.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No categories yet.</p>
              <Button
                onClick={() => setAddCategoryOpen(true)}
                variant="outline"
                className="gap-2"
              >
                <FolderPlus className="w-4 h-4" />
                Create your first category
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Dialogs */}
      <AddLinkDialog
        open={addLinkOpen}
        onOpenChange={(open) => {
          setAddLinkOpen(open);
          if (!open) setCreatingFeatured(false);
        }}
        categories={categories}
        defaultCategoryId={selectedCategoryId || undefined}
        defaultFeatured={creatingFeatured}
      />
      <EditLinkDialog
        open={editLinkOpen}
        onOpenChange={setEditLinkOpen}
        categories={categories}
        link={selectedLink}
      />
      <AddCategoryDialog
        open={addCategoryOpen}
        onOpenChange={setAddCategoryOpen}
      />
    </div>
  );
}

// Subcomponent to encapsulate DnD hooks per category item
function CategoryItem({
  category,
  onAddLink,
  onDeleteCategory,
  onEditLink,
  onDeleteLink,
  getPlatformUrl,
  isInternal,
  onReorderLinks,
}: {
  category: CategoryWithLinks;
  onAddLink: (categoryId: string) => void;
  onDeleteCategory: (categoryId: string) => void;
  onEditLink: (link: RouterOutputs['link']['getAll'][number]) => void;
  onDeleteLink: (linkId: string) => void;
  getPlatformUrl: (link: RouterOutputs['link']['getAll'][number]) => string;
  isInternal: (href: string | undefined | null) => boolean;
  onReorderLinks: (categoryId: string, ids: string[]) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: category.id, data: { type: 'category' } });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
  };

  // Determine link render order
  const orderedLinks: LinkItem[] = [...(category.links ?? [])].sort((a, b) => a.order - b.order);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "group/category will-change-transform transform-gpu",
      )}
    >
      {/* Category heading with actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span
            {...listeners}
            {...attributes}
            className="inline-flex items-center justify-center w-6 h-6 text-muted-foreground cursor-grab"
            style={{ touchAction: 'none' }}
          >
            <GripVertical className="w-4 h-4" />
          </span>
          <h2 className="text-2xl font-semibold m-0 text-foreground">
            {category.name}
          </h2>
          <span className="text-sm text-muted-foreground">
            ({category.links.length})
          </span>
        </div>

        {/* Desktop action buttons */}
        <div className="hidden sm:flex items-center gap-1">
          <Button
            onClick={() => onAddLink(category.id)}
            size="sm"
            variant="ghost"
            className="h-7 px-2"
          >
            <Plus className="w-3 h-3" />
          </Button>
          <Button
            onClick={() => onDeleteCategory(category.id)}
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-destructive hover:text-destructive"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>

        {/* Mobile dropdown menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild className="sm:hidden">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
              <MoreVertical className="w-4 h-4" />
              <span className="sr-only">Category actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAddLink(category.id)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Link
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDeleteCategory(category.id)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Category
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Links list with admin controls */}
      {orderedLinks.length > 0 ? (
        <VerticalSortable
          items={orderedLinks}
          getId={(l) => l.id}
          onReorder={(ids) => onReorderLinks(category.id, ids)}
          className="list-none pl-1 space-y-2"
        >
          {(link) => (
            <SortableLinkItem
              key={link.id}
              link={link}
              categoryId={category.id}
              getPlatformUrl={getPlatformUrl}
              isInternal={isInternal}
              onEditLink={onEditLink}
              onDeleteLink={onDeleteLink}
            />
          )}
        </VerticalSortable>
      ) : (
        <div className="text-muted-foreground text-sm py-4 border border-dashed rounded-md text-center">
          No links in this category{" "}
          <button
            onClick={() => onAddLink(category.id)}
            className="text-primary hover:underline"
          >
            Add one
          </button>
        </div>
      )}
    </li>
  );
}

// No custom DragOverlay; rely on dnd-kit sortable defaults

function SortableLinkItem({
  link,
  categoryId,
  getPlatformUrl,
  isInternal,
  onEditLink,
  onDeleteLink,
}: {
  link: LinkItem;
  categoryId: string;
  getPlatformUrl: (link: LinkItem) => string;
  isInternal: (href: string | undefined | null) => boolean;
  onEditLink: (link: LinkItem) => void;
  onDeleteLink: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: link.id, data: { type: 'link', categoryId } });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
  };

  const href = getPlatformUrl(link);
  const internal = isInternal(href);
  const TitleRow = (
    <div className="inline-flex items-center gap-2 text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary/60">
      <span className="font-medium">{link.name}</span>
      <ExternalLink className="w-3 h-3 opacity-50 group-hover/link:opacity-100" />
    </div>
  );
  const Description = link.description ? (
    <p className="mt-1 text-sm text-muted-foreground">{link.description}</p>
  ) : null;

  return (
    <li ref={setNodeRef} style={style} className="group/link flex items-start gap-2 will-change-transform transform-gpu">
      <span
        {...attributes}
        {...listeners}
        className="inline-flex items-center justify-center w-4 h-6 text-muted-foreground mt-0.5 cursor-grab"
        style={{ touchAction: 'none' }}
      >
        <GripVertical className="w-3 h-3" />
      </span>
      {internal ? (
        <NextLink href={href} className="block flex-1">
          {TitleRow}
          {Description}
        </NextLink>
      ) : (
        <a href={href} target="_blank" rel="noopener noreferrer" className="block flex-1">
          {TitleRow}
          {Description}
        </a>
      )}

      {/* Desktop action buttons */}
      <div className="hidden sm:flex items-center gap-1 transition-opacity self-center">
        <Button onClick={() => onEditLink(link)} size="sm" variant="ghost" className="h-6 px-1.5">
          <Edit2 className="w-3 h-3" />
        </Button>
        <Button onClick={() => onDeleteLink(link.id)} size="sm" variant="ghost" className="h-6 px-1.5 text-destructive hover:text-destructive">
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      {/* Mobile dropdown menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="sm:hidden self-center">
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
            <MoreVertical className="w-4 h-4" />
            <span className="sr-only">Link actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEditLink(link)}>
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDeleteLink(link.id)} className="text-destructive focus:text-destructive">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}
